import { AxleIconButton } from '@/components/buttons/AxleIconButton';
import { FloatingActionButton } from '@/components/buttons/FloatingActionButton';
import { AxleInfoField } from '@/components/displayFields/AxleInfoField';
import { QualificationChips } from '@/components/displayFields/chips';
import {
    AxleDialogActions,
    AxleDialogContent,
    AxleDialogDeleteButtonWithConfirmationDialog,
    AxleDialogPrimaryButton,
} from '@/components/layout/AxleDialog';
import { AxleTable } from '@/components/tables/AxleTable';
import { If } from '@/components/utils/conditionalComponents';
import {
    FloatRight,
    HorizontalStackInnerGrow,
    VerticalSpace,
} from '@/components/utils/linearLayouts';
import { AxleDialogShell } from '@/dialogs/AxleDialogShell/AxleDialogShell';
import { AxleDialogShellBehaviorProps } from '@/dialogs/AxleDialogShell/AxleDialogShell.types';
import { DialogType, useDialog } from '@/dialogs/DialogManager';
import { useAppConfig } from '@/fetching/api/appConfig';
import { useCurrentOrganization } from '@/fetching/api/organization';
import {
    useCreateQualification,
    useCreateQualificationOptions,
    useDeleteQualification,
    useEditQualification,
    useEditQualificationOptions,
    useListQualificationsByOrg,
} from '@/fetching/api/qualifications';
import { AxleErrorBoundary } from '@/fetching/errorBoundaries';
import {
    QualificationBasic,
    QualificationWithPermissions,
    useOrgPrefixedLabelForQualification,
    useOrgPrefixedLabelForQualificationGql,
} from '@/fetching/sharedSchemas/qualificationSchemas';
import { AxleSuspense, PAGE_BODY_SKELETON_HEIGHT } from '@/fetching/suspense';
import { InputState, useInputState, useJointInputState } from '@/utils/inputState';
import { ReturnComponent } from '@/utils/reactTypes';
import { gql } from '@apollo/client';
import AddIcon from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Settings from '@mui/icons-material/Settings';
import { Box, Typography } from '@mui/material';
import { useCallback, useState } from 'react';

import { AxleAutoCompleteFieldProps, AxleAutocompleteField } from './AxleAutocompleteField';
import { AxleTextField } from './AxleTextField';
import { OrgDropdown, useOrgDropdownState } from './OrgDropdown';
import { AxleAutocompleteMultiselectWithShortChips } from './multiselectFields';

const codeHelperText = (codeMaxLength: number) =>
    `Must fit in ${codeMaxLength} characters. Can include spaces.`;

const CreateQualificationDialogContent = ({
    close,
    orgToCreateOnId,
}: {
    close: () => void;
    orgToCreateOnId: string;
}): ReturnComponent => {
    const { codeMaxLength, orgOptions } = useCreateQualificationOptions();
    const createQualification = useCreateQualification();

    const orgState = useInputState<string>(orgToCreateOnId, {
        required: true,
    });
    const codeState = useInputState<string>('', {
        required: true,
        defaultHelperText: codeHelperText(codeMaxLength),
    });
    const descriptionState = useInputState<string>('');

    // The qualification model on the backend has three fields: name, code, and description.
    //  However, in practice, we just use the code field, and don’t really use the name field
    //  anywhere. So we just have the user enter the code, and use that as the name too.

    const { checkValid } = useJointInputState([orgState, codeState, descriptionState]);

    return (
        <>
            <AxleDialogContent>
                <If condition={orgOptions.length > 1}>
                    <AxleAutocompleteField
                        label={'Organization'}
                        inputState={orgState}
                        options={orgOptions}
                        optionToValue={(org) => org.uuid}
                        optionToLabel={(org) => org.name}
                    />
                </If>
                <AxleTextField label={'Code'} inputState={codeState} maxChars={codeMaxLength} />
                <AxleTextField label={'Description'} inputState={descriptionState} />
            </AxleDialogContent>
            <AxleDialogActions>
                <AxleDialogPrimaryButton
                    label={'Create'}
                    onClick={() => {
                        if (!checkValid()) {
                            return;
                        }
                        void createQualification(
                            {
                                org_uuid: orgState.value,
                                code: codeState.value,
                                description: descriptionState.value,
                            },
                            {
                                onSuccess: close,
                            },
                        );
                    }}
                />
            </AxleDialogActions>
        </>
    );
};

export interface CreateQualificationDialogProps extends AxleDialogShellBehaviorProps {
    onClose: () => void;
    orgToCreateOnId: string;
}

export const CreateQualificationDialog = ({
    onClose,
    orgToCreateOnId,
    ...behavior
}: CreateQualificationDialogProps): ReturnComponent => {
    // Held here so the dialog animates out before the manager unmounts it.
    const [isOpen, setIsOpen] = useState(true);
    const close = useCallback(() => setIsOpen(false), []);

    return (
        <AxleDialogShell
            {...behavior}
            dataTestId='create-qualification-dialog'
            open={isOpen}
            onClose={close}
            onTransitionExited={onClose}
            title='Create Qualification'
            disableBodyPadding
        >
            <AxleErrorBoundary>
                <AxleSuspense skeletonHeight={PAGE_BODY_SKELETON_HEIGHT} skeletonPadding={2}>
                    <CreateQualificationDialogContent
                        close={close}
                        orgToCreateOnId={orgToCreateOnId}
                    />
                </AxleSuspense>
            </AxleErrorBoundary>
        </AxleDialogShell>
    );
};

const EditQualificationDialogContent = ({
    close,
    qualification,
}: {
    close: () => void;
    qualification: QualificationBasic;
}): ReturnComponent => {
    const currentOrgId = useCurrentOrganization()?.id;
    const { codeMaxLength } = useEditQualificationOptions(qualification.uuid);
    const editQualification = useEditQualification(qualification.uuid);

    const deleteQualification = useDeleteQualification(qualification.uuid);

    const codeState = useInputState(qualification.code, {
        required: true,
        defaultHelperText: codeHelperText(codeMaxLength),
    });
    const descriptionState = useInputState(qualification.description);

    const { checkDifferent, checkValid } = useJointInputState([codeState, descriptionState]);

    return (
        <>
            <AxleDialogContent>
                <If condition={qualification.organization.uuid !== currentOrgId}>
                    <AxleInfoField label={'Organization'} value={qualification.organization.name} />
                </If>
                <AxleTextField label={'Code'} inputState={codeState} maxChars={codeMaxLength} />
                <AxleTextField label={'Description'} inputState={descriptionState} />
            </AxleDialogContent>
            <AxleDialogActions>
                <AxleDialogDeleteButtonWithConfirmationDialog
                    label={'Delete'}
                    confirmationText={'Are you sure you want to delete this criteria?'}
                    onClick={() => {
                        void deleteQualification(
                            {},
                            {
                                onSuccess: close,
                            },
                        );
                    }}
                />
                <AxleDialogPrimaryButton
                    label={'Save'}
                    onClick={() => {
                        if (!checkDifferent()) {
                            return;
                        }
                        if (!checkValid()) {
                            return;
                        }
                        void editQualification(
                            {
                                code: codeState.value,
                                description: descriptionState.value,
                            },
                            {
                                onSuccess: close,
                            },
                        );
                    }}
                />
            </AxleDialogActions>
        </>
    );
};

export interface EditQualificationDialogProps extends AxleDialogShellBehaviorProps {
    onClose: () => void;
    qualification: QualificationBasic;
}

export const EditQualificationDialog = ({
    onClose,
    qualification,
    ...behavior
}: EditQualificationDialogProps): ReturnComponent => {
    // Held here so the dialog animates out before the manager unmounts it.
    const [isOpen, setIsOpen] = useState(true);
    const close = useCallback(() => setIsOpen(false), []);

    return (
        <AxleDialogShell
            {...behavior}
            dataTestId='edit-qualifications-dialog'
            open={isOpen}
            onClose={close}
            onTransitionExited={onClose}
            title='Edit Criteria'
            disableBodyPadding
        >
            <AxleErrorBoundary>
                <AxleSuspense skeletonHeight={PAGE_BODY_SKELETON_HEIGHT} skeletonPadding={2}>
                    <EditQualificationDialogContent close={close} qualification={qualification} />
                </AxleSuspense>
            </AxleErrorBoundary>
        </AxleDialogShell>
    );
};

const ManageQualificationsDialogContent = (): ReturnComponent => {
    const appConfig = useAppConfig();
    const { openDialog } = useDialog();

    const currentOrgId = useCurrentOrganization()?.id;
    const { qualificationsByOrg } = useListQualificationsByOrg();
    const allOrgs = Object.values(qualificationsByOrg).map((value) => value.org);

    // Qualifications come back keyed by the organization the server authenticated
    //  the request as, which is not always one the response covers.
    const firstAvailableOrgId = allOrgs.length > 0 ? allOrgs[0].uuid : undefined;
    const orgDropdownState = useOrgDropdownState({
        showOptionToSelectAll: false,
        defaultOrgId:
            currentOrgId !== undefined && currentOrgId in qualificationsByOrg
                ? currentOrgId
                : firstAvailableOrgId,
    });
    const selectedOrgQualifications =
        orgDropdownState.value in qualificationsByOrg
            ? qualificationsByOrg[orgDropdownState.value]
            : undefined;
    const nativeQualifications = selectedOrgQualifications?.native_qualifications ?? [];
    const passthroughQualifications = selectedOrgQualifications?.passthrough_qualifications ?? [];

    return (
        <AxleDialogContent>
            <HorizontalStackInnerGrow spacing={2}>
                <OrgDropdown inputState={orgDropdownState} options={allOrgs} />
                <If condition={appConfig.qualification.create}>
                    <FloatRight>
                        <FloatingActionButton
                            icon={<AddIcon />}
                            text={'Create Criteria'}
                            onClick={() =>
                                openDialog(DialogType.CreateQualification, {
                                    orgToCreateOnId: orgDropdownState.value,
                                })
                            }
                        />
                    </FloatRight>
                </If>
            </HorizontalStackInnerGrow>

            <VerticalSpace spacing={2} />
            <AxleTable
                data={nativeQualifications}
                columns={[
                    {
                        label: 'Code',
                        field: 'code',
                    },
                    {
                        label: 'Description',
                        field: 'description',
                    },
                    {
                        rowToComponent: (qualification: QualificationWithPermissions) => (
                            <AxleIconButton
                                icon={<Edit />}
                                tooltip={'Edit Criteria'}
                                onClick={() =>
                                    openDialog(DialogType.EditQualification, { qualification })
                                }
                                enabled={qualification.permissions.write}
                                tooltipWhenDisabled={'You cannot edit this criteria.'}
                            />
                        ),
                    },
                ]}
            />

            <If condition={passthroughQualifications.length > 0}>
                <VerticalSpace spacing={4} />
                <Box>
                    <Typography mb={2}>
                        These criteria were passed down to you by a different organization. You can
                        assign them to your clinicians, but you cannot edit them:
                    </Typography>
                    <QualificationChips qualifications={passthroughQualifications} />
                </Box>
            </If>
        </AxleDialogContent>
    );
};

export interface ManageQualificationsDialogProps extends AxleDialogShellBehaviorProps {
    onClose: () => void;
}

export const ManageQualificationsDialog = ({
    onClose,
    ...behavior
}: ManageQualificationsDialogProps): ReturnComponent => {
    // Held here so the dialog animates out before the manager unmounts it.
    const [isOpen, setIsOpen] = useState(true);
    const close = useCallback(() => setIsOpen(false), []);

    return (
        <AxleDialogShell
            {...behavior}
            dataTestId='manage-eligibility-criteria-dialog'
            open={isOpen}
            onClose={close}
            onTransitionExited={onClose}
            title={'Manage Eligibility Criteria'}
            maxWidth={'md'}
            disableBodyPadding
        >
            <AxleErrorBoundary>
                <AxleSuspense skeletonHeight={PAGE_BODY_SKELETON_HEIGHT} skeletonPadding={2}>
                    <ManageQualificationsDialogContent />
                </AxleSuspense>
            </AxleErrorBoundary>
        </AxleDialogShell>
    );
};

export const QualificationsField = ({
    label,
    enabled = true,
    description,
    inputState,
    options,
}: {
    label: string;
    enabled?: boolean;
    description?: string;
    inputState: InputState<string[]>;
    options: QualificationBasic[];
}): ReturnComponent => {
    const qualificationLabel = useOrgPrefixedLabelForQualification(options);
    const { openDialog } = useDialog();

    return (
        <AxleAutocompleteMultiselectWithShortChips
            label={label}
            enabled={enabled}
            inputState={inputState}
            description={description}
            options={options}
            optionToLabel={(qualification) => qualificationLabel(qualification)}
            optionToValue={(qualification) => qualification.uuid}
            optionToToolTip={(qualification) => qualification.description}
            trailingButtons={
                <AxleIconButton
                    icon={<Settings />}
                    tooltip={'Manage Eligibility Criteria'}
                    onClick={() => openDialog(DialogType.ManageQualifications, {})}
                />
            }
        />
    );
};

export const QualificationField = ({
    label,
    enabled = true,
    inputState,
    options,
    ...rest
}: {
    label: string;
    enabled?: boolean;
    inputState: InputState<string | null>;
    options: QualificationBasic[];
} & Omit<
    AxleAutoCompleteFieldProps<string | null, QualificationBasic>,
    'inputState' | 'optionToLabel' | 'optionToValue'
>): ReturnComponent => {
    const qualificationLabel = useOrgPrefixedLabelForQualification(options);
    return (
        <AxleAutocompleteField
            {...rest}
            label={label}
            enabled={enabled}
            inputState={inputState}
            options={options}
            optionToLabel={(qualification) => qualificationLabel(qualification)}
            optionToValue={(qualification) => qualification.uuid}
        />
    );
};

export interface IQualificationOption {
    id: string;
    name: string;
    organization: {
        id: string;
        name: string;
    };
}

export const QualificationOptionFragment = gql`
    fragment QualificationOptionFragment on Qualification {
        id
        name
        organization {
            id
            name
        }
    }
`;

export const QualificationFieldGql = ({
    label,
    enabled = true,
    inputState,
    options,
    ...rest
}: {
    label: string;
    enabled?: boolean;
    inputState: InputState<string | null>;
    options: IQualificationOption[];
} & Omit<
    AxleAutoCompleteFieldProps<string | null, IQualificationOption>,
    'inputState' | 'optionToLabel' | 'optionToValue'
>): ReturnComponent => {
    const qualificationLabel = useOrgPrefixedLabelForQualificationGql(options);
    return (
        <AxleAutocompleteField
            {...rest}
            label={label}
            enabled={enabled}
            inputState={inputState}
            options={options}
            optionToLabel={(qualification) => qualificationLabel(qualification)}
            optionToValue={(qualification) => qualification.id}
        />
    );
};

export const QualificationsFilterField = ({
    fromSchedulePage,
    inputState,
}: {
    // Where is the qualifications filter being used? This affects the label.
    // When filtering by qualifications in the visits page, we are filtering
    // by the qualifications required on the visit's service(s). When filtering
    // by qualifications in the schedules page, we are filtering by the qualifications
    // of the clinicians, not the qualifications required on the individual shifts.
    // We want to explicitly label the qualification filter on the schedule page to
    // 'Clinician Qualifications' to make it clear how we are filtering.
    fromSchedulePage: boolean;
    inputState: InputState<string[]>;
}) => {
    const currentOrgId = useCurrentOrganization()?.id;
    const qualificationName = useOrgPrefixedLabelForQualification();

    // Load qualifications from backend.
    const { qualificationsByOrg } = useListQualificationsByOrg();
    const qualOptions =
        currentOrgId !== undefined && currentOrgId in qualificationsByOrg
            ? qualificationsByOrg[currentOrgId].native_qualifications
            : [];

    return (
        <AxleAutocompleteMultiselectWithShortChips
            label={fromSchedulePage ? 'Clinician Qualifications' : 'Qualifications'}
            inputState={inputState}
            options={qualOptions}
            optionToValue={(option) => option.uuid}
            optionToLabel={(option) => qualificationName(option)}
        />
    );
};
